/**
 * Imobilizado — gestão de bens, depreciação mensal e baixa.
 *
 * Telas:
 *   Lista (com KPIs no topo) → Cadastro (modal) → Ficha (modal com timeline) → Baixa (modal)
 *   Botão "Calcular depreciação do mês" → preview → confirmar contabilização
 */
import { useEffect, useState } from 'react';
import { PageHeader, Loading, Empty, Alert } from '../components/common';
import { fmtMoeda, fmtData, mesAtual, anosOpcoes, mesOpcoes } from '../lib/format';

import { authHeaders } from '../lib/auth-headers';
const API_BASE = import.meta.env.VITE_API_URL || '/api';

const CATEGORIAS_LABEL = {
  moveis: 'Móveis e Utensílios',
  imoveis: 'Imóveis',
  veiculos: 'Veículos',
  maquinas: 'Máquinas e Equipamentos',
  equipamentos_informatica: 'Informática',
  intangivel: 'Intangível',
  outros: 'Outros',
};

async function get(path) {
  const r = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  return r.json();
}
async function post(path, body) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.message || j.error || `HTTP ${r.status}`);
  return j;
}

export default function Imobilizado({ clienteId }) {
  const [bens, setBens] = useState([]);
  const [totais, setTotais] = useState({ qtd: 0, valor_original: 0, depreciacao_acumulada: 0, valor_contabil: 0 });
  const [loading, setLoading] = useState(true);
  const [mensagem, setMensagem] = useState(null);
  const [modalCadastro, setModalCadastro] = useState(false);
  const [modalFicha, setModalFicha] = useState(null);
  const [modalBaixa, setModalBaixa] = useState(null);
  const [modalCalc, setModalCalc] = useState(null);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    get(`/imobilizado/${clienteId}`)
      .then(r => { setBens(r.items); setTotais(r.totais); })
      .catch(e => setMensagem({ tipo: 'danger', txt: e.message }))
      .finally(() => setLoading(false));
  };
  useEffect(carregar, [clienteId]);

  if (!clienteId) return <Empty title="Selecione um cliente" />;
  if (loading)    return <Loading texto="Carregando bens…" />;

  const calcular = async () => {
    const comp = mesAtual();
    try {
      const preview = await post(`/imobilizado/${clienteId}/calcular?competencia=${comp}`);
      setModalCalc({ competencia: comp, ...preview });
    } catch (e) {
      setMensagem({ tipo: 'danger', txt: e.message });
    }
  };

  return (
    <>
      <PageHeader
        title="Ativo Imobilizado"
        sub="Cadastro de bens · Depreciação automática · Ficha contábil · Baixa"
        actions={
          <>
            <button onClick={calcular} disabled={bens.length === 0}>↻ Calcular depreciação do mês</button>
            <button className="primary" onClick={() => setModalCadastro(true)}>+ Novo bem</button>
          </>
        }
      />

      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <KPI label="Qtd bens ativos" valor={bens.filter(b => b.status === 'ativo').length} />
        <KPI label="Valor original" valor={fmtMoeda(totais.valor_original)} accent />
        <KPI label="Dep. acumulada" valor={fmtMoeda(totais.depreciacao_acumulada)} />
        <KPI label="Valor contábil" valor={fmtMoeda(totais.valor_contabil)} accent />
      </div>

      {/* Tabela */}
      {bens.length === 0 ? (
        <Empty title="Nenhum bem cadastrado ainda" sub="Clique em + Novo bem pra começar" />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="data" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Código</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Aquisição</th>
                <th style={{ textAlign: 'right' }}>Original</th>
                <th style={{ textAlign: 'right' }}>Dep. Acum.</th>
                <th style={{ textAlign: 'right' }}>Contábil</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bens.map(b => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => abrirFicha(b.id)}>
                  <td className="small" style={{ fontFamily: 'var(--font-data)' }}>{b.codigo_interno || `#${b.id}`}</td>
                  <td>{b.descricao}</td>
                  <td className="small muted">{CATEGORIAS_LABEL[b.categoria] || b.categoria}</td>
                  <td className="small">{fmtData(b.data_aquisicao)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(b.valor_original)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(b.depreciacao_acumulada)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)', fontWeight: 600 }}>{fmtMoeda(b.valor_contabil)}</td>
                  <td>
                    <span className={`badge badge--${b.status === 'ativo' ? 'success' : 'neutral'}`} style={{ fontSize: 9 }}>
                      {b.status}
                    </span>
                  </td>
                  <td onClick={e => { e.stopPropagation(); if (b.status === 'ativo') setModalBaixa(b); }}>
                    {b.status === 'ativo' && <button className="small danger">Baixar</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalCadastro && (
        <ModalCadastro
          clienteId={clienteId}
          onFechar={() => setModalCadastro(false)}
          onSalvo={() => { setModalCadastro(false); carregar(); setMensagem({ tipo: 'success', txt: '✓ Bem cadastrado' }); }}
        />
      )}
      {modalFicha && (
        <ModalFicha
          clienteId={clienteId}
          bemId={modalFicha}
          onFechar={() => setModalFicha(null)}
        />
      )}
      {modalBaixa && (
        <ModalBaixa
          clienteId={clienteId}
          bem={modalBaixa}
          onFechar={() => setModalBaixa(null)}
          onSalvo={() => { setModalBaixa(null); carregar(); setMensagem({ tipo: 'success', txt: '✓ Baixa registrada' }); }}
        />
      )}
      {modalCalc && (
        <ModalCalc
          clienteId={clienteId}
          preview={modalCalc}
          onFechar={() => setModalCalc(null)}
          onContabilizado={(r) => {
            setModalCalc(null);
            carregar();
            setMensagem({ tipo: 'success', txt: `✓ ${r.qtd_lctos} lançamentos de depreciação criados em ${r.competencia}` });
          }}
        />
      )}
    </>
  );

  function abrirFicha(id) { setModalFicha(id); }
}

function KPI({ label, valor, accent }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="small muted" style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 9 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: accent ? 'var(--gold)' : 'var(--text)', marginTop: 4, fontFamily: 'var(--font-data)' }}>
        {valor}
      </div>
    </div>
  );
}

// =============================================================================
// Modal: Cadastro de bem
// =============================================================================
function ModalCadastro({ clienteId, onFechar, onSalvo }) {
  const [form, setForm] = useState({
    descricao: '',
    categoria: 'equipamentos_informatica',
    data_aquisicao: new Date().toISOString().slice(0, 10),
    valor_original: '',
    valor_residual: 0,
    vida_util_meses: 60,
    nf_aquisicao: '',
    fornecedor: '',
    observacao: '',
  });
  const [categorias, setCategorias] = useState([]);
  const [contas, setContas] = useState([]);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    get('/imobilizado/_ref/categorias').then(r => setCategorias(r.items));
    get(`/plano-contas/cliente/${clienteId}?tipo=A&limit=2000`).then(r => setContas(r.items || []));
  }, [clienteId]);

  // Auto-preenche vida útil quando categoria muda
  const setCategoria = (cat) => {
    const ref = categorias.find(c => c.codigo === cat);
    setForm(f => ({
      ...f, categoria: cat,
      vida_util_meses: ref?.vida_util_meses || f.vida_util_meses,
    }));
  };

  const salvar = async () => {
    setErro(null); setSalvando(true);
    try {
      await post(`/imobilizado/${clienteId}`, {
        ...form,
        valor_original: Number(form.valor_original),
        valor_residual: Number(form.valor_residual || 0),
        vida_util_meses: Number(form.vida_util_meses),
        conta_ativo_id: form.conta_ativo_id ? Number(form.conta_ativo_id) : undefined,
        conta_dep_acumulada_id: form.conta_dep_acumulada_id ? Number(form.conta_dep_acumulada_id) : undefined,
        conta_despesa_dep_id: form.conta_despesa_dep_id ? Number(form.conta_despesa_dep_id) : undefined,
      });
      onSalvo();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase titulo="Novo bem do imobilizado" onFechar={onFechar} largura={700}>
      {erro && <Alert tipo="danger">{erro}</Alert>}

      <Field label="Descrição">
        <input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} placeholder="Ex: Notebook Dell Latitude 5440" />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Categoria">
          <select value={form.categoria} onChange={e => setCategoria(e.target.value)}>
            {categorias.map(c => (
              <option key={c.codigo} value={c.codigo}>
                {CATEGORIAS_LABEL[c.codigo] || c.codigo} ({c.vida_util_meses}m)
              </option>
            ))}
          </select>
        </Field>
        <Field label="Código interno (opcional)">
          <input value={form.codigo_interno || ''} onChange={e => setForm({ ...form, codigo_interno: e.target.value })} placeholder="PAT-001" />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Data aquisição">
          <input type="date" value={form.data_aquisicao} onChange={e => setForm({ ...form, data_aquisicao: e.target.value })} />
        </Field>
        <Field label="Valor original (R$)">
          <input type="number" step="0.01" value={form.valor_original} onChange={e => setForm({ ...form, valor_original: e.target.value })} />
        </Field>
        <Field label="Vida útil (meses)">
          <input type="number" value={form.vida_util_meses} onChange={e => setForm({ ...form, vida_util_meses: e.target.value })} />
        </Field>
      </div>

      <h3 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--gold)', marginTop: 16, marginBottom: 6 }}>Contas contábeis</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Conta ativo">
          <ContasSelect contas={contas} value={form.conta_ativo_id || ''} onChange={v => setForm({ ...form, conta_ativo_id: v })} />
        </Field>
        <Field label="Conta dep. acumulada">
          <ContasSelect contas={contas} value={form.conta_dep_acumulada_id || ''} onChange={v => setForm({ ...form, conta_dep_acumulada_id: v })} />
        </Field>
        <Field label="Conta despesa dep.">
          <ContasSelect contas={contas} value={form.conta_despesa_dep_id || ''} onChange={v => setForm({ ...form, conta_despesa_dep_id: v })} />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Fornecedor"><input value={form.fornecedor} onChange={e => setForm({ ...form, fornecedor: e.target.value })} /></Field>
        <Field label="NF aquisição"><input value={form.nf_aquisicao} onChange={e => setForm({ ...form, nf_aquisicao: e.target.value })} /></Field>
      </div>

      <div className="flex" style={{ marginTop: 16, gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onFechar} disabled={salvando}>Cancelar</button>
        <button className="primary" onClick={salvar} disabled={salvando || !form.descricao || !form.valor_original}>
          {salvando ? 'Salvando…' : 'Cadastrar bem'}
        </button>
      </div>
    </ModalBase>
  );
}

// =============================================================================
// Modal: Ficha do bem
// =============================================================================
function ModalFicha({ clienteId, bemId, onFechar }) {
  const [data, setData] = useState(null);
  useEffect(() => { get(`/imobilizado/${clienteId}/${bemId}`).then(setData); }, [clienteId, bemId]);
  if (!data) return <ModalBase titulo="Ficha do bem" onFechar={onFechar}><Loading /></ModalBase>;
  const { bem, depreciacoes, baixas } = data;

  return (
    <ModalBase titulo={`Ficha: ${bem.descricao}`} onFechar={onFechar} largura={900}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        <KPI label="Valor original" valor={fmtMoeda(bem.valor_original)} />
        <KPI label="Dep. acumulada" valor={fmtMoeda(bem.depreciacao_acumulada)} />
        <KPI label="Valor contábil" valor={fmtMoeda(bem.valor_contabil)} accent />
        <KPI label="Última dep." valor={bem.ultima_competencia || '—'} />
      </div>

      <div className="small muted" style={{ marginBottom: 10, fontFamily: 'var(--font-data)' }}>
        {bem.codigo_interno && `${bem.codigo_interno} · `}
        {CATEGORIAS_LABEL[bem.categoria]} · Aquisição {fmtData(bem.data_aquisicao)} · Vida útil {bem.vida_util_meses}m
        · Status: <strong>{bem.status}</strong>
      </div>

      <h3 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--gold)', marginTop: 14, marginBottom: 6 }}>
        Histórico de depreciação ({depreciacoes.length} mes(es))
      </h3>
      {depreciacoes.length === 0 ? (
        <div className="small muted">Ainda não há depreciação contabilizada.</div>
      ) : (
        <table className="data" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Competência</th>
              <th style={{ textAlign: 'right' }}>Depreciação</th>
              <th style={{ textAlign: 'right' }}>Acumulada</th>
              <th style={{ textAlign: 'right' }}>Residual</th>
              <th>Status</th>
              <th>Lcto</th>
            </tr>
          </thead>
          <tbody>
            {depreciacoes.map(d => (
              <tr key={d.id}>
                <td className="small" style={{ fontFamily: 'var(--font-data)' }}>{d.competencia}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(d.valor_depreciacao)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(d.valor_acumulado)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(d.valor_residual_apos)}</td>
                <td><span className="badge badge--neutral" style={{ fontSize: 9 }}>{d.status}</span></td>
                <td className="small">{d.lancamento_id ? `#${d.lancamento_id}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {baixas.length > 0 && (
        <>
          <h3 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--danger)', marginTop: 14, marginBottom: 6 }}>Baixas</h3>
          <table className="data" style={{ width: '100%' }}>
            <thead><tr><th>Data</th><th>Motivo</th><th>Valor venda</th><th>Residual</th><th>Ganho/Perda</th></tr></thead>
            <tbody>
              {baixas.map(b => (
                <tr key={b.id}>
                  <td className="small">{fmtData(b.data_baixa)}</td>
                  <td>{b.motivo}</td>
                  <td style={{ textAlign: 'right' }}>{b.valor_venda ? fmtMoeda(b.valor_venda) : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoeda(b.valor_residual_baixa)}</td>
                  <td style={{ textAlign: 'right', color: Number(b.ganho_perda) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                    {fmtMoeda(b.ganho_perda)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="flex" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button onClick={onFechar}>Fechar</button>
      </div>
    </ModalBase>
  );
}

// =============================================================================
// Modal: Baixa de bem
// =============================================================================
function ModalBaixa({ clienteId, bem, onFechar, onSalvo }) {
  const [form, setForm] = useState({
    data_baixa: new Date().toISOString().slice(0, 10),
    motivo: 'venda',
    valor_venda: '',
    observacao: '',
  });
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!confirm(`Confirma baixa de "${bem.descricao}" (valor contábil ${fmtMoeda(bem.valor_contabil)})?`)) return;
    setSalvando(true); setErro(null);
    try {
      await post(`/imobilizado/${clienteId}/${bem.id}/baixar`, {
        ...form,
        valor_venda: form.motivo === 'venda' ? Number(form.valor_venda || 0) : undefined,
      });
      onSalvo();
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  };

  return (
    <ModalBase titulo={`Baixar bem: ${bem.descricao}`} onFechar={onFechar}>
      {erro && <Alert tipo="danger">{erro}</Alert>}
      <Alert tipo="warning">Valor contábil atual: <strong>{fmtMoeda(bem.valor_contabil)}</strong> · Operação não pode ser desfeita.</Alert>

      <Field label="Data da baixa">
        <input type="date" value={form.data_baixa} onChange={e => setForm({ ...form, data_baixa: e.target.value })} />
      </Field>
      <Field label="Motivo">
        <select value={form.motivo} onChange={e => setForm({ ...form, motivo: e.target.value })}>
          <option value="venda">Venda</option>
          <option value="sucateamento">Sucateamento</option>
          <option value="perda">Perda</option>
          <option value="transferencia">Transferência</option>
          <option value="obsolescencia">Obsolescência</option>
          <option value="outros">Outros</option>
        </select>
      </Field>
      {form.motivo === 'venda' && (
        <Field label="Valor de venda (R$)">
          <input type="number" step="0.01" value={form.valor_venda} onChange={e => setForm({ ...form, valor_venda: e.target.value })} />
        </Field>
      )}
      <Field label="Observação">
        <textarea rows="2" value={form.observacao} onChange={e => setForm({ ...form, observacao: e.target.value })} />
      </Field>

      <div className="flex" style={{ marginTop: 14, gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onFechar} disabled={salvando}>Cancelar</button>
        <button className="danger" onClick={salvar} disabled={salvando}>{salvando ? 'Baixando…' : 'Confirmar baixa'}</button>
      </div>
    </ModalBase>
  );
}

// =============================================================================
// Modal: Cálculo de depreciação do mês
// =============================================================================
function ModalCalc({ clienteId, preview, onFechar, onContabilizado }) {
  const [contabilizando, setContabilizando] = useState(false);
  const [erro, setErro] = useState(null);

  const contabilizar = async () => {
    setContabilizando(true); setErro(null);
    try {
      const r = await post(`/imobilizado/${clienteId}/contabilizar?competencia=${preview.competencia}`);
      onContabilizado(r);
    } catch (e) { setErro(e.message); } finally { setContabilizando(false); }
  };

  return (
    <ModalBase titulo={`Depreciação ${preview.competencia}`} onFechar={onFechar} largura={900}>
      {erro && <Alert tipo="danger">{erro}</Alert>}

      <div className="flex" style={{ gap: 16, marginBottom: 12, fontFamily: 'var(--font-data)' }}>
        <div><span className="muted small">Bens</span><br/><strong>{preview.qtd_bens}</strong></div>
        <div><span className="muted small">A depreciar</span><br/><strong>{preview.qtd_calculados}</strong></div>
        <div><span className="muted small">Já contabilizados</span><br/><strong>{preview.qtd_ja_contabilizados}</strong></div>
        <div style={{ marginLeft: 'auto' }}>
          <span className="muted small">Total</span><br/>
          <strong style={{ color: 'var(--gold)', fontSize: 18 }}>{fmtMoeda(preview.total_depreciacao)}</strong>
        </div>
      </div>

      <table className="data" style={{ width: '100%' }}>
        <thead>
          <tr><th>Bem</th><th style={{ textAlign: 'right' }}>Original</th><th style={{ textAlign: 'right' }}>Dep. mês</th><th style={{ textAlign: 'right' }}>Acum. após</th><th>Status</th></tr>
        </thead>
        <tbody>
          {preview.detalhes.map(d => (
            <tr key={d.bem_id}>
              <td>
                <div>{d.descricao}</div>
                <div className="small muted">{d.codigo_interno} · {CATEGORIAS_LABEL[d.categoria]}</div>
              </td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(d.valor_original)}</td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)', color: d.valor_depreciacao > 0 ? 'var(--gold)' : 'var(--text-3)' }}>
                {d.valor_depreciacao > 0 ? fmtMoeda(d.valor_depreciacao) : '—'}
              </td>
              <td style={{ textAlign: 'right', fontFamily: 'var(--font-data)' }}>{fmtMoeda(d.acumulado_apos)}</td>
              <td>
                {d.ja_contabilizado ? <span className="badge badge--success" style={{ fontSize: 9 }}>já contab.</span>
                 : d.motivo ? <span className="small muted">{d.motivo}</span>
                 : <span className="badge badge--neutral" style={{ fontSize: 9 }}>pronto</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex" style={{ marginTop: 14, gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onFechar} disabled={contabilizando}>Cancelar</button>
        <button className="primary" onClick={contabilizar} disabled={contabilizando || preview.qtd_calculados === 0}>
          {contabilizando ? 'Contabilizando…' : `Contabilizar ${preview.qtd_calculados} lançamento(s)`}
        </button>
      </div>
    </ModalBase>
  );
}

// =============================================================================
// Helpers de UI
// =============================================================================
function ModalBase({ titulo, onFechar, largura = 600, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20,
    }} onClick={onFechar}>
      <div className="card" style={{ width: '95%', maxWidth: largura, maxHeight: '90vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>{titulo}</h2>
          <div style={{ flex: 1 }} />
          <button onClick={onFechar}>Fechar</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label className="small muted" style={{ display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function ContasSelect({ contas, value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— Selecione —</option>
      {contas.map(c => (
        <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>
      ))}
    </select>
  );
}
